import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CitationService } from "../../../service/citation/citation.service";
import { Response } from "@angular/http";
import { FormGroup, Validators, FormBuilder } from "@angular/forms";
import { MedicalExamsService } from "../../../service/medical-exams/medical-exams.service";
import { formatDate } from "../../../../../theme/functions/formatDate.function";
import { NotificationsService } from "../../../../../theme/services/notifications/notifications.service";
import { getLocalISOString } from "../../../../../theme/functions/getLocalISOString";

@Component({
  selector: "app-valid-medical-exams",
  templateUrl: "./valid-medical-exams.component.html",
  styleUrls: ["./valid-medical-exams.component.scss"],
})
export class ValidMedicalExamsComponent implements OnInit {
  examId: string;
  citationData: any;
  medicalExamDetailsForm: FormGroup;
  isExamDataLocked: boolean = false;
  laboratoryId: number = null;
  isDisabled: boolean = false;
  medicalExamOptions: any[] = [];
  selectedExams: any[] = [];
  rescheduleForm: FormGroup;
  displayRescheduleDialog: boolean;
  showExamSelection: boolean = false;
  promiseForm: Promise<any>;
  promiseNotifyCandidate: Promise<any>;

  constructor(
    private route: ActivatedRoute,
    public citationService: CitationService,
    private fb: FormBuilder,
    private medicalExamsService: MedicalExamsService,
    private notificationsService: NotificationsService
  ) { }

  ngOnInit() {
    this.examId = this.route.snapshot.paramMap.get("id");
    this.initMedicalExamDetailsForm();
    this.fetchCitationData(
      this.examId,
      "candidate,requisition,requisition.company,requisition.profile_survey"
    );
    this.medicalExamDetailsForm
      .get("medicalExams")
      .valueChanges.subscribe((examIds: number[]) => {
        this.updateSelectedExams(examIds);
      });
    this.initRescheduleForm();
  }

  initMedicalExamDetailsForm() {
    this.medicalExamDetailsForm = this.fb.group({
      examDate: ["", Validators.required],
      examTime: ["", Validators.required],
      recommendations: [""],
      requiredPreparation: [""],
      send_automatic_notifications_lab: [true],
      notes_for_laboratory: ['', [Validators.maxLength(255)]],
      medicalExams: [[]],
    });
  }

  fetchCitationData(id: string, _with?: string): void {
    this.citationService.infoCitationMedicalExam(Number(id), _with).subscribe(
      (response: Response) => {
        this.citationData = response.json();
        this.loadExamData();
      },
      (error) => {
        console.error("Error al obtener la cita:", error);
      }
    );
  }

  loadExamData() {
    if (this.citationData) {
      // Procesar fecha y hora
      const examDateTime = this.citationData.date_medical_appointment;
      if (examDateTime) {
        const dateTime = new Date(examDateTime);
        this.medicalExamDetailsForm.patchValue({
          examDate: dateTime,
          examTime: dateTime,
        });
      }

      if (this.citationData.laboratory && this.citationData.laboratory.id) {
        this.laboratoryId = this.citationData.laboratory.id;
        this.loadMedicalExams(this.laboratoryId);
        this.showExamSelection = true;
      } else {
        this.showExamSelection = false;
      }

      if (this.citationData.send_automatic_notifications_lab == null)
        this.citationData.send_automatic_notifications_lab = true;
      // Cargar otros datos
      this.medicalExamDetailsForm.patchValue({
        send_automatic_notifications_lab: this.citationData.send_automatic_notifications_lab,
        notes_for_laboratory: this.citationData.notes_for_laboratory,
        recommendations: this.citationData.recommendations_medical_exams,
        requiredPreparation:
          this.citationData.required_preparation_medical_exams,
        medicalExams: this.citationData.medicalExams,
      });

      this.isExamDataLocked = !!this.citationData.date_medical_appointment;
    }
  }

  onLaboratoryChange(selectedLaboratory: any) {
    if (selectedLaboratory && selectedLaboratory.id) {
      this.laboratoryId = selectedLaboratory.id;
      this.loadMedicalExams(this.laboratoryId);
      this.showExamSelection = true;
    } else {
      this.showExamSelection = false;
    }
  }

  loadMedicalExams(labId: number) {
    if (labId) {
      this.medicalExamsService.getMedicalExamsLaboratory(labId).subscribe(
        (response) => {
          this.medicalExamOptions = response.json().data || [];
          this.updateSelectedExams(this.citationData.medicalExams)
        },
        (error) => {
          console.error("Error loading medical exams:", error);
          this.medicalExamOptions = [];
        }
      );
    }
  }

  // Método para actualizar los exámenes seleccionados
  private updateSelectedExams(examIds: number[]): void {
    if (!examIds || examIds.length === 0) {
      this.selectedExams = [];
      return;
    }

    // Filtrar los exámenes seleccionados de las opciones disponibles
    this.selectedExams = this.medicalExamOptions.filter((option) =>
      examIds.includes(option.id)
    );
  }

  // Método para eliminar un examen
  removeExam(exam: any): void {
    const currentExams = this.medicalExamDetailsForm.get("medicalExams")
      .value as number[];
    const updatedExams = currentExams.filter((id) => id !== exam.id);
    this.medicalExamDetailsForm.get("medicalExams").setValue(updatedExams);
  }

  // Método para formatear el precio CORREGIDO
  formatPrice(price: number | string): string {
    if (price === null || price === undefined) return "$0";

    // Convertir a número
    const numValue = typeof price === "string" ? parseFloat(price) : price;

    // Formatear directamente (sin dividir por 1000)
    return (
      "$" +
      numValue
        .toLocaleString("es-CO", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/,/g, ".")
    ); // Cambiar comas por puntos para formato colombiano
  }

  // Método para calcular el total CORREGIDO
  getFormattedTotal(): string {
    const total = this.selectedExams.reduce((acc, exam) => {
      const price =
        typeof exam.price === "string" ? parseFloat(exam.price) : exam.price;
      return acc + (price || 0);
    }, 0);

    return this.formatPrice(total);
  }

  prepareSubmitData() {
    const formValue = this.medicalExamDetailsForm.value;

    // Combinar fecha y hora
    const examDate = new Date(formValue.examDate);
    const examTime = new Date(formValue.examTime);

    examDate.setHours(examTime.getHours());
    examDate.setMinutes(examTime.getMinutes());

    // Usar el nuevo método formatDate
    const date_medical_appointment = formatDate(examDate);

    return {
      date_medical_appointment,
      lab_medical_exams: this.laboratoryId,
      medicalExams: this.selectedExams.map((exam) => exam.id),
      recommendations_medical_exams: formValue.recommendations,
      required_preparation_medical_exams: formValue.requiredPreparation,
      send_automatic_notifications_lab: formValue.send_automatic_notifications_lab,
      notes_for_laboratory: formValue.notes_for_laboratory,
      start_medical_exams: getLocalISOString(),
    };
  }

  generateExamOrder() {
    if (this.medicalExamDetailsForm.valid && this.selectedExams.length > 0) {
      const submitData = this.prepareSubmitData();

      // Convertimos el Observable a Promesa manualmente
      this.promiseForm = new Promise((resolve, reject) => {
        this.citationService
          .generateOrdenMedicalExam(submitData, +this.examId)
          .subscribe(
            (response) => {
              this.notificationsService.growlNotify(
                "success",
                "Orden de exámenes generada correctamente"
              );
              resolve(response);
              this.isExamDataLocked = true;
            },
            (error) => {
              console.error("Error al generar orden:", error);
              this.notificationsService.growlNotify(
                "error",
                "No se pudo generar la orden de exámenes"
              );
              reject(error);
            }
          );
      });
    } else {
      this.notificationsService.growlNotify(
        "warn",
        "Por favor complete todos los campos requeridos y seleccione al menos un examen"
      );
    }
  }

  sendNotifyCandidate() {
    if (this.medicalExamDetailsForm.valid) {
      const formValues = this.medicalExamDetailsForm.value;

      const examDate = new Date(formValues.examDate);
      const examTime = new Date(formValues.examTime);

      const formattedDate = this.formatDateToDDMMYYYY(examDate);
      const formattedTime = this.formatTimeTo12Hour(examTime);

      const notifyPayload = {
        exam_date: formattedDate,
        exam_time: formattedTime,
        recommendations_medical_exams: formValues.recommendations,
        required_preparation_medical_exams: formValues.requiredPreparation,
      };

      this.promiseNotifyCandidate = new Promise((resolve, reject) => {
        this.medicalExamsService
          .notifyMedicalExamToCandidate(notifyPayload, +this.examId)
          .subscribe(
            (response) => {
              this.notificationsService.growlNotify(
                "success",
                "Candidato notificado exitosamente para los exámenes médicos"
              );
              resolve(response);
            },
            (error) => {
              this.notificationsService.growlNotify(
                "error",
                "Ocurrió un error al enviar la notificación"
              );
              reject(error);
            }
          );
      });
    } else {
      this.notificationsService.growlNotify(
        "warn",
        "Por favor complete todos los campos requeridos del formulario de exámenes"
      );
    }
  }

  // Métodos auxiliares
  private formatDateToDDMMYYYY(date: Date): string {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatTimeTo12Hour(date: Date): string {
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  // Inicializar formulario de reprogramación
  initRescheduleForm() {
    this.rescheduleForm = this.fb.group({
      newDate: ["", Validators.required],
      newTime: ["", Validators.required],
      notes_reprogramming_medical_exams: [""],
    });
  }

  // Método para abrir el diálogo de reprogramación
  rescheduleExam() {
    this.displayRescheduleDialog = true;
    // Opcional: Pre-cargar la fecha y hora actuales
    this.rescheduleForm.patchValue({
      newDate: this.medicalExamDetailsForm.get("examDate").value,
      newTime: this.medicalExamDetailsForm.get("examTime").value,
      notes_reprogramming_medical_exams: this.citationData.notes_reprogramming_medical_exams,
    });
  }

  // Confirmar la reprogramación
  confirmReschedule() {
    if (this.rescheduleForm.valid) {
      const newDate = this.rescheduleForm.get("newDate").value;
      const newTime = this.rescheduleForm.get("newTime").value;
      const notes_reprogramming_medical_exams = this.rescheduleForm.get("notes_reprogramming_medical_exams").value;
      const currentExams = this.medicalExamDetailsForm.get("medicalExams")
        .value as number[];

      // Unificar fecha y hora en un solo Date
      const dateTime = new Date(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        newTime.getHours(),
        newTime.getMinutes()
      );

      const date_medical_appointment = formatDate(dateTime);

      const submitData = {
        date_medical_appointment,
        notes_reprogramming_medical_exams: notes_reprogramming_medical_exams || "",
        medicalExams: currentExams,
      };

      this.promiseForm = new Promise((resolve, reject) => {
        this.citationService
          .reprogrammingOrdenMedicalExam(submitData, +this.examId)
          .subscribe(
            (response) => {
              this.notificationsService.growlNotify(
                "success",
                "Examen médico reprogramado correctamente Y Se Notifico Al Candidato"
              );
              resolve(response);
              this.isExamDataLocked = true;
              this.displayRescheduleDialog = false;
              this.fetchCitationData(
                this.examId,
                "candidate,requisition,requisition.company,requisition.profile_survey"
              );
            },
            (error) => {
              console.error("Error al reprogramar el examen:", error);
              this.notificationsService.growlNotify(
                "error",
                "No se pudo reprogramar el examen médico"
              );
              reject(error);
            }
          );
      });
    }
  }

  // Limpiar el formulario al cerrar el diálogo
  onRescheduleDialogHide() {
    this.rescheduleForm.reset();
  }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/employee/pages/medical-exams/valid-medical-exams/valid-medical-exams.component.ts